"use client";

import { buildClearedFilters } from "@/lib/filters/clear-filters";
import {
  HotelSummary,
  SearchSessionStatus,
  SearchSessionV3,
  SearchSessionV2,
  SuggestedSearch,
  SuggestedSearchesResponse,
  SuggestedSearchType,
} from "@/generated/search.openapi";
import { handleError } from "@/lib/apiUtils";
import { SuggestedSearchesGetSearchTypesEnum } from "@/generated/search.openapi";

import { distanceBetweenPointsInMiles } from "@/lib/mapUtils";

import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { MapElements, getHotelMapElements } from "../[searchId]/helper";
import { LoyaltyProgramEnum } from "@/generated/email_parser.openapi";
import { getDisplayNightlyRate, getTransferPartnersMapping } from "@/lib/utils";

import { createLatLngBounds } from "@/lib/mapUtils";

import type {
  HotelSearchFilters,
  BedType as SharedBedType,
} from "@/lib/filters/types.generated";
import { useOptionalFavoriteHotels } from "@/app/context/favoriteHotelsProvider";

export type BedType = SharedBedType;

export type BenefitsMetrics = "Cash Only" | "Points Only";
// Add type for loyalty brand that includes Independent
export type LoyaltyBrandOptions = LoyaltyProgramEnum | "Independent";

/**
 * Unified filter type for hotel search.
 * Imported from the shared filter system — extended with web-specific
 * computed fields (amenities, availableImageCategories).
 */
export type FilterOption = HotelSearchFilters & {
  amenities?: string[];
  availableAmenities?: string[];
  availableImageCategories?: string[];
  favoritesOnly?: boolean;
};

export type HotelData = HotelSummary & {
  distance?: number;
  mapElements?: MapElements;
};

export type TransferPartnersMapping = {
  [key: string]: LoyaltyProgramEnum[];
};

type SearchContextType = {
  searchResponse?: SearchSessionV2;
  setSearchResponse?: Dispatch<SetStateAction<SearchSessionV2 | undefined>>;
  searchResponseLoading: boolean;
  setSearchResponseLoading?: Dispatch<SetStateAction<boolean>>;
  initialBounds?: google.maps.LatLngBounds;
  setInitialBounds?: Dispatch<
    SetStateAction<google.maps.LatLngBounds | undefined>
  >;
  setRecommendedViewport?: Dispatch<
    SetStateAction<google.maps.LatLngBounds | undefined>
  >;
  recommendedViewport?: google.maps.LatLngBounds;
  loadingScreen: boolean;
  setLoadingScreen?: Dispatch<SetStateAction<boolean>>;
  hotels: HotelData[];
  filters?: FilterOption;
  setFilters?: Dispatch<SetStateAction<FilterOption>>;
  resetFilters?: () => void;
  transferPartnersMapping?: TransferPartnersMapping;
  // New suggested searches state
  suggestedSearches?: SuggestedSearchesResponse;
  setSuggestedSearches?: Dispatch<
    SetStateAction<SuggestedSearchesResponse | undefined>
  >;
  suggestedSearchesLoading: boolean;
  setSuggestedSearchesLoading?: Dispatch<SetStateAction<boolean>>;
  selectedForComparison: number[];
  toggleComparisonSelection: (id: number) => void;
  clearComparisonSelection: () => void;
  suggestedSearchContext?: SuggestedSearchType;
  setSuggestedSearchContext?: Dispatch<
    SetStateAction<SuggestedSearchType | undefined>
  >;
};

const MAX_COMPARISON_SELECTION = 3;

const SearchContext = createContext<SearchContextType>({
  loadingScreen: true,
  searchResponseLoading: false,
  hotels: [],
  suggestedSearchesLoading: false,
  selectedForComparison: [],
  toggleComparisonSelection: () => {},
  clearComparisonSelection: () => {},
});

const initialSearchFilters: FilterOption = {
  sortBy: "recommended",
  averageGuestRating: [0, 10],
  showUnavailable: false,
};

export default function SearchProvider({ children }: PropsWithChildren) {
  const favorites = useOptionalFavoriteHotels();
  const favoritesEnabled = favorites?.enabled === true;
  const favoriteSubject = favorites?.subject;
  const registerSearchCandidates = favorites?.registerSearchCandidates;
  const resetSearchMembership = favorites?.resetSearchMembership;
  const [searchResponse, setSearchResponse] = useState<SearchSessionV2>();
  const [searchResponseLoading, setSearchResponseLoading] = useState(true);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [initialBounds, setInitialBounds] = useState<
    google.maps.LatLngBounds | undefined
  >();
  const [recommendedViewport, setRecommendedViewport] = useState<
    google.maps.LatLngBounds | undefined
  >();
  const [filters, setFilters] = useState<FilterOption>(initialSearchFilters);
  const [filterSubject, setFilterSubject] = useState(favoriteSubject);
  if (filterSubject !== favoriteSubject) {
    setFilterSubject(favoriteSubject);
    setFilters((current) =>
      current.favoritesOnly ? { ...current, favoritesOnly: undefined } : current
    );
  }
  // New suggested searches state
  const [suggestedSearches, setSuggestedSearches] =
    useState<SuggestedSearchesResponse>();
  const [suggestedSearchesLoading, setSuggestedSearchesLoading] =
    useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState<number[]>(
    []
  );
  const [suggestedSearchContext, setSuggestedSearchContext] =
    useState<SuggestedSearchType>();

  const toggleComparisonSelection = useCallback((id: number) => {
    setSelectedForComparison((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARISON_SELECTION) return prev;
      return [...prev, id];
    });
  }, []);

  const clearComparisonSelection = useCallback(() => {
    setSelectedForComparison([]);
  }, []);

  // Clear selection when search results change (new search)
  const searchId = searchResponse?.id;
  useEffect(() => {
    setSelectedForComparison([]);
    setFilters((current) =>
      current.favoritesOnly ? { ...current, favoritesOnly: undefined } : current
    );
  }, [searchId]);

  const previousHotelsRef = useRef<HotelData[]>([]);

  const hotels: HotelData[] = useMemo(() => {
    if (searchResponse?.results?.hotels?.items && searchResponse.request) {
      return (
        searchResponse.results.hotels.items?.map((hotel) => ({
          ...hotel,
          distance: distanceBetweenPointsInMiles(
            {
              lat: searchResponse.request?.hotels?.searchLocation.coordinates
                ?.latitude as number,
              lng: searchResponse.request?.hotels?.searchLocation.coordinates
                ?.longitude as number,
            },
            {
              lat: hotel.coordinates?.latitude as number,
              lng: hotel.coordinates?.longitude as number,
            }
          ),
          mapElements: (() => {
            try {
              return getHotelMapElements(hotel, searchResponse);
            } catch {
              // Google Maps may not be loaded yet (race condition: search results
              // can arrive before the Maps JS library finishes initialising).
              // Return undefined and let HotelMapView create the elements lazily
              // once the map — and therefore Google Maps — is ready.
              return undefined;
            }
          })(),
        })) || []
      );
    } else {
      return [];
    }
  }, [searchResponse]);

  useEffect(() => {
    if (!favoritesEnabled) {
      setFilters((current) =>
        current.favoritesOnly
          ? { ...current, favoritesOnly: undefined }
          : current
      );
      resetSearchMembership?.();
      return;
    }
    if (!searchResponse?.id || !registerSearchCandidates) return;
    const terminal =
      searchResponse.sessionStatus === SearchSessionStatus.Complete;
    registerSearchCandidates(
      searchResponse.id,
      hotels
        .map((hotel) => hotel.vervotechPropertyId)
        .filter(
          (propertyId): propertyId is number => typeof propertyId === "number"
        ),
      { terminal, replace: terminal }
    );
  }, [
    favoritesEnabled,
    favoriteSubject,
    hotels,
    registerSearchCandidates,
    resetSearchMembership,
    searchResponse?.id,
    searchResponse?.sessionStatus,
  ]);

  // Cleanup info windows for hotels that dropped out of the list.
  // Hotels that persist across polling updates must keep their info windows
  // mounted — the map's marker click handlers close over those hotel
  // references, and unmounting the React root would leave clicks rendering
  // an empty popup.
  useEffect(() => {
    const currentHotelIds = new Set(hotels.map((h) => h.vervotechPropertyId));
    const hotelsToCleanup: HotelData[] = [];

    previousHotelsRef.current.forEach((prevHotel) => {
      if (!currentHotelIds.has(prevHotel.vervotechPropertyId)) {
        hotelsToCleanup.push(prevHotel);
      }
    });

    // Defer cleanup to avoid "unmount during render" warning
    if (hotelsToCleanup.length > 0) {
      setTimeout(() => {
        hotelsToCleanup.forEach((hotel) => {
          const infoWindowWithCleanup = hotel.mapElements
            ?.infoWindow as google.maps.InfoWindow & { __cleanup?: () => void };
          if (infoWindowWithCleanup?.__cleanup) {
            try {
              infoWindowWithCleanup.__cleanup();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn("Failed to cleanup info window:", e);
            }
          }
        });
      }, 0);
    }

    previousHotelsRef.current = hotels;
  }, [hotels]);

  // Cleanup ALL info windows on unmount. Separate from the hotels-change
  // effect above because a useEffect cleanup fires on every dep change,
  // not just on unmount — running it per-poll would tear down React roots
  // for hotels the marker click handlers still reference.
  useEffect(() => {
    return () => {
      const allHotels = [...previousHotelsRef.current];
      previousHotelsRef.current = [];

      setTimeout(() => {
        allHotels.forEach((hotel) => {
          const infoWindowWithCleanup = hotel.mapElements
            ?.infoWindow as google.maps.InfoWindow & { __cleanup?: () => void };
          if (infoWindowWithCleanup?.__cleanup) {
            try {
              infoWindowWithCleanup.__cleanup();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn("Failed to cleanup info window:", e);
            }
          }
        });
      }, 0);
    };
  }, []);

  // Utility to get percentile
  function getPercentile(arr: number[], p: number) {
    if (!arr.length) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const idx = Math.floor((p / 100) * (sorted.length - 1));
    return sorted[idx];
  }

  const filterDefaults = useMemo(() => {
    const distanceArray: number[] =
      hotels?.map((item) => item.distance ?? 0) || [];
    const cashHistogram: number[] =
      hotels
        ?.map(
          (item) =>
            getDisplayNightlyRate(
              item,
              filters?.isRefundable,
              filters?.roomType,
              filters?.bedType
            ) as number
        )
        .filter((item) => !!item) || [];
    const pointsHistogram: number[] =
      hotels
        ?.map((item) => item.lowestNightlyPoints as number)
        .filter((item) => !!item) || [];

    const distanceLimit = [
      Math.min(...distanceArray),
      Math.max(...distanceArray),
    ] as [number, number];
    const cashRangeLimit = [
      Math.min(...cashHistogram),
      Math.max(...cashHistogram),
    ] as [number, number];
    const pointsRangeLimit = [
      Math.min(...pointsHistogram),
      Math.max(...pointsHistogram),
    ] as [number, number];

    const cashCap = getPercentile(cashHistogram, 98);
    const pointsCap = getPercentile(pointsHistogram, 98);

    return {
      availableAmenities: searchResponse?.results?.hotels?.availableAmenities,
      availableImageCategories:
        searchResponse?.results?.hotels?.availableImageCategories,
      cashHistogram,
      cashRangeLimit,
      pointsHistogram,
      pointsRangeLimit,
      distanceLimit,
      cashCap,
      pointsCap,
    };
  }, [
    hotels,
    searchResponse?.results?.hotels?.availableAmenities,
    searchResponse?.results?.hotels?.availableImageCategories,
    filters?.isRefundable,
    filters?.roomType,
    filters?.bedType,
  ]);

  useEffect(() => {
    if (filterDefaults) {
      setFilters((prev) => {
        // ---
        // We always preserve the user's last explicit choice (selectedCashRange, selectedPointRange)
        // even if it is out of bounds of the current search results. This ensures that user intent is not lost
        // when results/limits change incrementally (e.g., as results load in pages or are refined).
        // The clamped value is used for filtering and display, but the user's intent is preserved and will be used
        // as soon as it becomes valid again.
        // ---
        const userInputCash = prev.selectedCashRange ?? prev.clampedCashRange;
        const userInputPoints =
          prev.selectedPointRange ?? prev.clampedPointRange;
        // Clamp for display/filtering
        let clampedCashRange = userInputCash;
        if (clampedCashRange && filterDefaults.cashRangeLimit) {
          clampedCashRange = [
            Math.max(
              filterDefaults.cashRangeLimit[0],
              Math.min(filterDefaults.cashRangeLimit[1], clampedCashRange[0])
            ),
            Math.max(
              filterDefaults.cashRangeLimit[0],
              Math.min(filterDefaults.cashRangeLimit[1], clampedCashRange[1])
            ),
          ];
        }
        let clampedPointsRange = userInputPoints;
        if (clampedPointsRange && filterDefaults.pointsRangeLimit) {
          clampedPointsRange = [
            Math.max(
              filterDefaults.pointsRangeLimit[0],
              Math.min(
                filterDefaults.pointsRangeLimit[1],
                clampedPointsRange[0]
              )
            ),
            Math.max(
              filterDefaults.pointsRangeLimit[0],
              Math.min(
                filterDefaults.pointsRangeLimit[1],
                clampedPointsRange[1]
              )
            ),
          ];
        }
        return {
          ...prev,
          ...filterDefaults,
          selectedCashRange: userInputCash,
          selectedPointRange: userInputPoints,
          clampedCashRange: clampedCashRange,
          clampedPointRange: clampedPointsRange,
        };
      });
    }
  }, [filterDefaults]);

  useEffect(() => {
    if (searchResponse?.promptResponse?.viewport && !initialBounds) {
      createLatLngBounds(searchResponse?.promptResponse?.viewport)
        .then((bounds) => {
          setInitialBounds?.(bounds);
        })
        .catch(() => {
          // Silently handle error - bounds will be set when Google Maps loads
        });
    }
  }, [initialBounds, searchResponse?.promptResponse?.viewport]);

  useEffect(() => {
    if (
      searchResponse?.results?.hotels?.recommendedViewport &&
      !recommendedViewport
    ) {
      createLatLngBounds(searchResponse?.results?.hotels?.recommendedViewport)
        .then((bounds) => {
          setRecommendedViewport?.(bounds);
        })
        .catch(() => {
          // Silently handle error - bounds will be set when Google Maps loads
        });
    }
  }, [
    recommendedViewport,
    searchResponse?.results?.hotels?.recommendedViewport,
  ]);

  const transferPartnersMapping = useMemo(
    () =>
      searchResponse?.results?.hotels?.availableTransferPartners
        ? getTransferPartnersMapping(
            searchResponse?.results?.hotels?.availableTransferPartners
          )
        : undefined,
    [searchResponse?.results?.hotels?.availableTransferPartners]
  );

  return (
    <SearchContext.Provider
      value={{
        searchResponse,
        setSearchResponse,
        searchResponseLoading,
        setSearchResponseLoading,
        initialBounds,
        setInitialBounds,
        recommendedViewport,
        setRecommendedViewport,
        loadingScreen,
        setLoadingScreen,
        filters,
        setFilters,
        resetFilters: () =>
          setFilters((prev) => buildClearedFilters(prev, initialSearchFilters)),
        hotels,
        transferPartnersMapping,
        suggestedSearches,
        setSuggestedSearches,
        suggestedSearchesLoading,
        setSuggestedSearchesLoading,
        selectedForComparison,
        toggleComparisonSelection,
        clearComparisonSelection,
        suggestedSearchContext,
        setSuggestedSearchContext,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

const useSearch = () => {
  const {
    searchResponse,
    setSearchResponse,
    searchResponseLoading,
    setSearchResponseLoading,
    initialBounds,
    setInitialBounds,
    recommendedViewport,
    setRecommendedViewport,
    loadingScreen,
    setLoadingScreen,
    filters,
    setFilters,
    resetFilters,
    hotels,
    transferPartnersMapping,
    suggestedSearches,
    setSuggestedSearches,
    suggestedSearchesLoading,
    setSuggestedSearchesLoading,
    selectedForComparison,
    toggleComparisonSelection,
    clearComparisonSelection,
    suggestedSearchContext,
    setSuggestedSearchContext,
  } = useContext(SearchContext);

  const refreshIndividualSuggestedSearch = useCallback(
    async (searchId: string, userId?: string) => {
      if (!userId) return null;

      try {
        const response = await fetch(
          `/api/search/suggested_searches/${searchId}?refresh=true`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.suggestedSearch; // The API returns SuggestedSearchResponse with a suggestedSearch property
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error refreshing suggested search ${searchId}:`, error);
        return null;
      }
    },
    []
  );

  const fetchSuggestedSearches = useCallback(
    async (userId?: string) => {
      if (!userId || !setSuggestedSearchesLoading || !setSuggestedSearches)
        return;

      try {
        setSuggestedSearchesLoading(true);

        const searchTypes = [
          SuggestedSearchesGetSearchTypesEnum.RecentSearches,
          SuggestedSearchesGetSearchTypesEnum.UpcomingTrips,
          SuggestedSearchesGetSearchTypesEnum.UpcomingHolidays,
          // SuggestedSearchesGetSearchTypesEnum.PopularDestinations,
          // SuggestedSearchesGetSearchTypesEnum.PopularOnGondola,
        ];

        const params = new URLSearchParams();
        searchTypes.forEach((type) => params.append("search_types", type));
        params.append("limit", "6");

        const response = await fetch(
          `/api/search/suggested_searches?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: SuggestedSearchesResponse = await response.json();

        // Set initial data with only non-expired searches
        setSuggestedSearches(data);
      } catch (error) {
        handleError(error, { reportError: true, silent: true });
      } finally {
        setSuggestedSearchesLoading(false);
      }
    },
    [setSuggestedSearchesLoading, setSuggestedSearches]
  );

  const refreshExpiredResults = useCallback(
    async (search: SuggestedSearch, userId?: string) => {
      if (!search?.v3Request || !userId || !setSuggestedSearches) return;

      try {
        const response = await fetch("/api/search/v3", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(search.v3Request),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: SearchSessionV3 = await response.json();

        setSuggestedSearches((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            suggestedSearches: prev.suggestedSearches.map((s) =>
              s.id === search.id
                ? {
                    ...s,
                    topResults: data.searchResults?.results || [],
                    resultsExpireAt: new Date(
                      Date.now() + 24 * 60 * 60 * 1000
                    ).toISOString(), // Extend expiration by 24 hours
                  }
                : s
            ),
          };
        });
      } catch (error) {
        handleError(error, { reportError: true });
      }
    },
    [setSuggestedSearches]
  );

  return {
    searchResponse,
    setSearchResponse,
    searchResponseLoading,
    setSearchResponseLoading,
    initialBounds,
    setInitialBounds,
    recommendedViewport,
    setRecommendedViewport,
    loadingScreen,
    setLoadingScreen,
    filters,
    setFilters,
    resetFilters,
    hotels,
    transferPartnersMapping,
    suggestedSearches,
    setSuggestedSearches,
    suggestedSearchesLoading,
    setSuggestedSearchesLoading,
    fetchSuggestedSearches,
    refreshExpiredResults,
    refreshIndividualSuggestedSearch,
    selectedForComparison,
    toggleComparisonSelection,
    clearComparisonSelection,
    suggestedSearchContext,
    setSuggestedSearchContext,
  };
};

export { useSearch };
